import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { DocumentSignFieldInput, DocumentSignFieldResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseFieldsReturn {
  fields: DocumentSignFieldResponse[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useFields(
  envelopeId: string | null | undefined,
  fileId: string | null | undefined,
): UseFieldsReturn {
  const enabled = Boolean(envelopeId && fileId);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey:
      envelopeId && fileId
        ? queryKeys.documentSign.fields(envelopeId, fileId)
        : ['documentSign-fields-noop'],
    queryFn: () => {
      if (!envelopeId || !fileId) return Promise.resolve({ fields: [] });
      return api.listDocumentSignFields(envelopeId, fileId);
    },
    enabled,
  });

  return {
    fields: data?.fields ?? [],
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load fields' : null,
    refresh: () => {
      refetch();
    },
  };
}

export function useSetFields(envelopeId: string, fileId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fields: DocumentSignFieldInput[]) => {
      const result = await api.setDocumentSignFields(envelopeId, fileId, { fields });
      return result.fields;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.documentSign.fields(envelopeId, fileId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelope(envelopeId) });
    },
  });
}
